// Personalization utilities for RTI calculations and action cards

interface UserProfile {
  attachmentStyle: 'secure' | 'anxious' | 'avoidant' | 'fearful' | 'unsure';
  relationshipGoal: 'save' | 'understand' | 'stability' | 'attraction' | 'general';
  pastPatterns: string[];
}

interface RTIWeights {
  initiation: number;
  latency: number;
  emotionalDensity: number;
  availability: number;
  withdrawal: number;
}

// Default equal weights (20% each)
const DEFAULT_WEIGHTS: RTIWeights = {
  initiation: 0.20,
  latency: 0.20,
  emotionalDensity: 0.20,
  availability: 0.20,
  withdrawal: 0.20,
};

/**
 * Calculate personalized RTI weights based on user profile
 */
export function getPersonalizedWeights(): RTIWeights {
  const profileData = localStorage.getItem('tension_user_profile');
  
  if (!profileData) {
    return DEFAULT_WEIGHTS;
  }

  const profile: UserProfile = JSON.parse(profileData);
  const weights = { ...DEFAULT_WEIGHTS };

  // Adjust weights based on attachment style
  switch (profile.attachmentStyle) {
    case 'anxious':
      // Anxious attachment: emphasize initiation and availability (over-pursuit patterns)
      weights.initiation = 0.30;
      weights.availability = 0.25;
      weights.latency = 0.15;
      weights.emotionalDensity = 0.15;
      weights.withdrawal = 0.15;
      break;

    case 'avoidant':
      // Avoidant attachment: emphasize withdrawal and emotional density
      weights.withdrawal = 0.30;
      weights.emotionalDensity = 0.25;
      weights.initiation = 0.15;
      weights.latency = 0.15;
      weights.availability = 0.15;
      break;

    case 'fearful':
      // Fearful-avoidant: balanced but emphasize push-pull indicators
      weights.initiation = 0.25;
      weights.withdrawal = 0.25;
      weights.availability = 0.20;
      weights.latency = 0.15;
      weights.emotionalDensity = 0.15;
      break;

    case 'secure':
      // Secure: balanced weights, slight emphasis on communication
      weights.latency = 0.25;
      weights.emotionalDensity = 0.25;
      weights.initiation = 0.17;
      weights.availability = 0.17;
      weights.withdrawal = 0.16;
      break;

    default:
      // Unsure: keep default weights
      break;
  }

  // Further adjust based on past patterns
  if (profile.pastPatterns.includes('over_pursue') || profile.pastPatterns.includes('anxious_texting')) {
    weights.initiation = Math.min(weights.initiation + 0.05, 0.35);
  }

  if (profile.pastPatterns.includes('lose_myself') || profile.pastPatterns.includes('need_reassurance')) {
    weights.availability = Math.min(weights.availability + 0.05, 0.35);
  }

  if (profile.pastPatterns.includes('withdraw') || profile.pastPatterns.includes('self_sabotage')) {
    weights.withdrawal = Math.min(weights.withdrawal + 0.05, 0.35);
  }

  if (profile.pastPatterns.includes('attract_unavailable')) {
    weights.latency = Math.min(weights.latency + 0.05, 0.30);
  }

  // Normalize to ensure weights sum to 1.0
  const sum = Object.values(weights).reduce((a, b) => a + b, 0);
  Object.keys(weights).forEach((key) => {
    weights[key as keyof RTIWeights] = weights[key as keyof RTIWeights] / sum;
  });

  return weights;
}

/**
 * Calculate personalized RTI score using custom weights
 */
export function calculatePersonalizedRTI(components: {
  initiation: number;
  latency: number;
  emotionalDensity: number;
  availability: number;
  withdrawal: number;
}): number {
  const weights = getPersonalizedWeights();

  const weightedScore = 
    components.initiation * weights.initiation +
    components.latency * weights.latency +
    components.emotionalDensity * weights.emotionalDensity +
    components.availability * weights.availability +
    components.withdrawal * weights.withdrawal;

  return Math.round(weightedScore);
}

/**
 * Get personalized action card recommendations based on profile
 */
export function getPersonalizedActionPriorities(rtiScore: number): {
  primary: string;
  secondary: string;
  focus: string[];
} {
  const profileData = localStorage.getItem('tension_user_profile');
  
  if (!profileData) {
    return getDefaultPriorities(rtiScore);
  }

  const profile: UserProfile = JSON.parse(profileData);

  // High tension (61+)
  if (rtiScore > 60) {
    switch (profile.relationshipGoal) {
      case 'save':
        return {
          primary: 'restraint',
          secondary: 'self-care',
          focus: ['reduce contact by 40%', 'emergency no-contact if needed', 'focus on external activities'],
        };
      case 'attraction':
        return {
          primary: 'restraint',
          secondary: 'mystery',
          focus: ['create scarcity', 'be less available', 'build independent life'],
        };
      case 'stability':
        return {
          primary: 'restraint',
          secondary: 'communication',
          focus: ['pause over-functioning', 'allow space', 'evaluate compatibility'],
        };
      default:
        return {
          primary: 'restraint',
          secondary: 'reflection',
          focus: ['reduce pursuit', 'self-focus', 'pattern awareness'],
        };
    }
  }

  // Elastic zone (36-60)
  if (rtiScore > 35) {
    switch (profile.relationshipGoal) {
      case 'save':
        return {
          primary: 'balance',
          secondary: 'reconnection',
          focus: ['match their energy', 'quality over quantity', 'address issues calmly'],
        };
      case 'attraction':
        return {
          primary: 'balance',
          secondary: 'playfulness',
          focus: ['light interactions', 'show value', 'maintain mystery'],
        };
      default:
        return {
          primary: 'balance',
          secondary: 'consistency',
          focus: ['maintain equilibrium', 'avoid over-pursuit', 'stay grounded'],
        };
    }
  }

  // Stable zone (0-35)
  switch (profile.relationshipGoal) {
    case 'stability':
      return {
        primary: 'maintain',
        secondary: 'deepen',
        focus: ['keep doing what works', 'gradual deepening', 'mutual investment'],
      };
    case 'attraction':
      return {
        primary: 'maintain',
        secondary: 'build',
        focus: ['maintain healthy dynamic', 'create shared experiences', 'stay interesting'],
      };
    default:
      return {
        primary: 'maintain',
        secondary: 'growth',
        focus: ['stable foundation', 'vulnerability when appropriate', 'balanced reciprocity'],
      };
  }
}

function getDefaultPriorities(rtiScore: number) {
  if (rtiScore > 60) {
    return {
      primary: 'restraint',
      secondary: 'self-care',
      focus: ['reduce pursuit', 'self-focus', 'create space'],
    };
  }
  if (rtiScore > 35) {
    return {
      primary: 'balance',
      secondary: 'consistency',
      focus: ['match energy', 'stay grounded', 'avoid over-function'],
    };
  }
  return {
    primary: 'maintain',
    secondary: 'growth',
    focus: ['keep steady', 'healthy patterns', 'mutual effort'],
  };
}

/**
 * Get personalized insight message based on profile
 */
export function getPersonalizedInsight(attachmentStyle: string, rtiScore: number): string | null {
  if (attachmentStyle === 'anxious' && rtiScore > 60) {
    return 'Your anxious attachment may be amplifying relationship concerns. Consider if your anxiety is creating tension independent of their behavior.';
  }

  if (attachmentStyle === 'avoidant' && rtiScore > 50) {
    return 'Notice if you\'re withdrawing as a defense mechanism. Avoidant patterns can create self-fulfilling distance.';
  }

  if (attachmentStyle === 'fearful' && rtiScore > 55) {
    return 'Your push-pull dynamic may be confusing to your partner. Consider consistent communication about your needs.';
  }

  return null;
}
